/**
 * MfaSetupFlow - UI Layer
 * Flujo in-app de alta del segundo factor (post-token). Pantalla completa centrada.
 * Toda la lógica vive en el hook useMfaSetup (Clean Architecture).
 * Paso `choose`: elección de método. Paso `show-totp`: wizard con QR + recovery codes.
 */

import { Button } from '@/components/ui/Button';
import { OtpInput } from '@/components/auth/OtpInput';
import { useMfaSetup } from '@/hooks/useMfaSetup';

interface MfaSetupFlowProps {
  onLogout?: () => void;
}

export function MfaSetupFlow({ onLogout }: MfaSetupFlowProps) {
  const {
    step, showEmail, showTotp, showSms, totp, savedCodes, setSavedCodes,
    code, setCode, error, loading, chooseEmail, chooseSms, startTotp, verifyTotp, back,
  } = useMfaSetup();

  const errorBlock = error && (
    <div
      className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive"
      role="alert"
    >
      {error}
    </div>
  );

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4">
      <div className="w-full max-w-md space-y-6">
        {step === 'choose' && (
          /* ── Paso 1: elección de método ── */
          <>
            <div className="text-center">
              <h1 className="text-3xl font-bold tracking-tight text-foreground">
                Configurá tu segundo factor
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">
                Para continuar necesitás activar la verificación en dos pasos (MFA).
              </p>
            </div>

            {errorBlock}

            <div className="space-y-3 rounded-lg border border-border bg-card p-8 shadow-sm">
              {showEmail && (
                <Button fullWidth disabled={loading} onClick={() => void chooseEmail()}>
                  Recibir código por correo
                </Button>
              )}
              {showTotp && (
                <Button fullWidth disabled={loading} onClick={() => void startTotp()}>
                  Usar app de autenticación
                </Button>
              )}
              {showSms && (
                <Button fullWidth disabled={loading} onClick={() => void chooseSms()}>
                  Recibir código por SMS
                </Button>
              )}
            </div>

            {onLogout && (
              <div className="text-center">
                <Button variant="ghost" onClick={onLogout}>
                  Cerrar sesión
                </Button>
              </div>
            )}
          </>
        )}

        {step === 'show-totp' && totp && (
          /* ── Paso 2: wizard TOTP (QR + recovery codes) ── */
          <div className="space-y-6 rounded-lg border border-border bg-card p-8 shadow-sm">
            <div className="text-center">
              <h1 className="text-2xl font-bold tracking-tight text-foreground">
                Escaneá el código QR
              </h1>
            </div>

            {errorBlock}

            <img
              src={totp.qrDataUrl}
              width={200}
              height={200}
              alt="Código QR"
              className="mx-auto"
            />

            <div className="space-y-1 text-center">
              <p className="text-sm text-muted-foreground">
                O ingresá esta clave en tu app:
              </p>
              <code className="block break-all rounded-md bg-muted px-3 py-2 text-sm">
                {totp.secret}
              </code>
            </div>

            <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4">
              <p className="mb-3 text-sm font-medium text-destructive">
                Guardá estos códigos de recuperación (se muestran una sola vez)
              </p>
              <ul className="grid grid-cols-2 gap-2">
                {totp.recoveryCodes.map((rc) => (
                  <li key={rc} className="font-mono text-sm text-foreground">
                    {rc}
                  </li>
                ))}
              </ul>
            </div>

            <label className="flex items-center gap-2 text-sm text-foreground">
              <input
                type="checkbox"
                checked={savedCodes}
                onChange={(e) => setSavedCodes(e.target.checked)}
              />
              Guardé mis códigos de recuperación
            </label>

            <OtpInput
              value={code}
              onChange={setCode}
              maxLength={6}
              onSubmit={verifyTotp}
              disabled={!savedCodes || loading}
              label="Código de la app"
            />

            <Button
              fullWidth
              isLoading={loading}
              disabled={!savedCodes || code.length !== 6 || loading}
              onClick={() => void verifyTotp()}
            >
              Activar
            </Button>

            <div className="text-center">
              <Button variant="ghost" onClick={back} disabled={loading}>
                Volver
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
